import { observer } from 'mobx-react-lite'
import React, { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { Alert, Button, Col, Empty, Input, Modal, Popconfirm, Radio, Row, Segmented, Select, Skeleton, Spin, Table, Tag, Tooltip } from 'antd'
import {
    BankOutlined,
    CheckCircleFilled,
    CheckOutlined,
    ClockCircleFilled,
    CloseCircleFilled,
    CreditCardOutlined,
    FileTextOutlined,
    LinkOutlined,
    QrcodeOutlined,
    SafetyCertificateOutlined,
} from '@ant-design/icons'
import i from '../../translations/i'
import { Titles } from '../../layouts/Titles'
import controller from './billing.controller'
import { SubscriptionCard } from './SubscriptionCard'
import {
    BillingMethod,
    days,
    formatDate,
    formatDateTime,
    formatMoney,
    methodLabel,
    months,
    paymentStatusColor,
} from './billing.helpers'
import './billing.less'

const currency = (): string => controller.overview?.currency ?? 'RUB'

const isEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

/** Текущая подписка: в разделе оплаты кнопка сразу открывает оформление */
const CurrentSubscription = observer(({ onChoose }: { onChoose: () => void }) => {
    const subscription = controller.overview?.subscription
    if (!subscription) return null

    const current = controller.plans.find((plan: any) => plan.level === subscription.level)
    const canRenew = !!current && !!controller.price(current, controller.months)

    return (
        <SubscriptionCard
            subscription={subscription}
            action={controller.canPay && (canRenew
                ? <Button type="primary" size="large" onClick={() => controller.openCheckout(current)}>{i.t('billing:subscription.renew')}</Button>
                : <Button type="primary" size="large" onClick={onChoose}>{i.t('billing:subscription.choose')}</Button>
            )}
        />
    )
})

/** Партнёрский проект, биллинг выключен или не настроен — платить через панель нельзя */
const Unavailable = observer(() => {
    const availability = controller.overview?.availability
    if (!availability || availability.available) return null

    const support = controller.overview?.support
    const contacts = (contact: any) => [contact?.phone, contact?.email].filter(Boolean).join(' · ')

    if (availability.reason === 'partner') {
        return (
            <Alert
                className="billing-alert"
                type="info"
                showIcon
                message={i.t('billing:unavailable.partnerTitle', { name: availability.partner?.name ?? '' })}
                description={i.t('billing:unavailable.partnerText', { contacts: contacts(availability.partner) || '—' })}
            />
        )
    }

    return (
        <></>
    )
})

/** Заявка на счёт ждёт обработки — показываем её, чтобы клиент не отправлял вторую */
const OpenInvoice = observer(() => {
    const invoice = controller.overview?.open_invoice
    if (!invoice) return null

    return (
        <Alert
            className="billing-alert"
            type="warning"
            showIcon
            icon={<FileTextOutlined />}
            message={i.t('billing:invoice.openTitle', { number: invoice.number, date: formatDateTime(invoice.created_at) })}
            description={i.t('billing:invoice.openText', {
                plan: invoice.plan_name,
                period: months(invoice.months),
                amount: formatMoney(invoice.amount, invoice.currency),
                email: invoice.payer_email,
            })}
            action={
                <div className="billing-alert__actions">
                    {controller.overview?.is_staff &&
                        <Popconfirm title={i.t('billing:invoice.confirmQuestion')} onConfirm={() => controller.confirmInvoice(invoice.uuid)} okText={i.t('billing:invoice.confirm')} cancelText={i.t('billing:cancel')}>
                            <Button size="small" type="primary">{i.t('billing:invoice.confirm')}</Button>
                        </Popconfirm>
                    }
                    <Popconfirm title={i.t('billing:invoice.cancelQuestion')} onConfirm={() => controller.cancelInvoice(invoice.uuid)} okText={i.t('billing:invoice.cancel')} cancelText={i.t('billing:back')}>
                        <Button size="small">{i.t('billing:invoice.cancel')}</Button>
                    </Popconfirm>
                </div>
            }
        />
    )
})

/** Возврат со страницы оплаты */
const PaymentResult = observer(({ onClose }: { onClose: () => void }) => {
    const { state, payment } = controller.result
    if (!state) return null

    if (state === 'checking') {
        return (
            <div className="billing-result billing-result--checking">
                <Spin />
                <div>
                    <div className="billing-result__title">{i.t('billing:result.checking')}</div>
                    <div className="billing-result__text">{i.t('billing:result.checkingHint')}</div>
                </div>
            </div>
        )
    }

    const plan = controller.plans.find((item: any) => item.level === payment?.plan_level)

    const content = {
        paid: {
            icon: <CheckCircleFilled />,
            title: i.t('billing:result.paid'),
            text: i.t('billing:result.paidText', { plan: payment?.plan_name, date: formatDate(payment?.period_end) })
                + (payment?.payer_email ? ' ' + i.t('billing:result.receipt', { email: payment.payer_email }) : ''),
        },
        failed: {
            icon: <CloseCircleFilled />,
            title: i.t('billing:result.failed'),
            text: i.t('billing:result.failedText'),
        },
        waiting: {
            icon: <ClockCircleFilled />,
            title: i.t('billing:result.waiting'),
            text: i.t('billing:result.waitingText'),
        },
    }[state]

    return (
        <div className={`billing-result billing-result--${state}`}>
            <div className="billing-result__icon">{content.icon}</div>
            <div className="billing-result__body">
                <div className="billing-result__title">{content.title}</div>
                <div className="billing-result__text">{content.text}</div>
            </div>
            <div className="billing-result__actions">
                {state === 'failed' && plan && controller.price(plan, payment.months) &&
                    <Button type="primary" onClick={() => { controller.setMonths(payment.months); controller.openCheckout(plan); onClose() }}>
                        {i.t('billing:result.retry')}
                    </Button>
                }
                <Button onClick={onClose}>{i.t('billing:close')}</Button>
            </div>
        </div>
    )
})

const PeriodSwitch = observer(() => {
    const periods = controller.periods
    if (periods.length < 2) return null

    return (
        <Segmented
            size="large"
            value={controller.months}
            onChange={(value) => controller.setMonths(Number(value))}
            options={periods.map((period: number) => {
                const discount = controller.maxDiscount(period)

                return {
                    value: period,
                    label: (
                        <span className="billing-period">
                            {months(period)}
                            {discount > 0 && <span className="billing-period__discount">−{discount}%</span>}
                        </span>
                    ),
                }
            })}
        />
    )
})

const PlanCard = observer(({ plan }: { plan: any }) => {
    const subscription = controller.overview?.subscription
    const isCurrent = subscription?.level === plan.level && subscription?.status !== 'none'
    const price = controller.price(plan)

    return (
        <div className={`billing-plan${plan.popular ? ' billing-plan--popular' : ''}${isCurrent ? ' billing-plan--current' : ''}`}>
            <div className="billing-plan__head">
                <div className="billing-plan__name">{plan.name}</div>
                {isCurrent
                    ? <span className="billing-plan__chip billing-plan__chip--current">{i.t('billing:plans.current')}</span>
                    : plan.popular && <span className="billing-plan__chip">{i.t('billing:plans.popular')}</span>
                }
            </div>
            {plan.description && <div className="billing-plan__description">{plan.description}</div>}

            {price
                ? <div className="billing-plan__price">
                    <div className="billing-plan__monthly">
                        {formatMoney(price.monthly_amount, currency())}
                        <span>{i.t('billing:plans.perMonth')}</span>
                    </div>
                    <div className="billing-plan__total">
                        {price.months > 1
                            ? <>
                                {i.t('billing:plans.total', { amount: formatMoney(price.amount, currency()), period: months(price.months) })}
                                {price.discount_percent > 0 && price.full_amount &&
                                    <span className="billing-plan__full">{formatMoney(price.full_amount, currency())}</span>
                                }
                            </>
                            : i.t('billing:plans.monthly')
                        }
                    </div>
                </div>
                : <div className="billing-plan__price billing-plan__price--none">{i.t('billing:plans.noPeriod', { period: months(controller.months) })}</div>
            }

            <ul className="billing-plan__features">
                {plan.features.map((feature: string, index: number) => (
                    <li key={index}><CheckOutlined />{feature}</li>
                ))}
            </ul>

            <Button
                block
                size="large"
                type={plan.popular || isCurrent ? 'primary' : 'default'}
                disabled={!price || !controller.canPay}
                onClick={() => controller.openCheckout(plan)}
            >
                {isCurrent ? i.t('billing:plans.renew') : i.t('billing:plans.choose')}
            </Button>
        </div>
    )
})

/** Что произойдёт с подпиской после оплаты — текст из расчёта сервера */
const QuoteExplanation = observer(() => {
    const { quote, quoteLoading, quoteError } = controller.checkout

    if (quoteError) return <Alert type="error" showIcon message={quoteError} />
    if (!quote || quoteLoading) return <Skeleton active paragraph={{ rows: 1 }} title={false} />

    const params = {
        date: formatDate(quote.ends_at),
        from: quote.current?.plan_name ?? '',
        to: quote.plan_name,
        credit: days(quote.credit_days),
    }

    return (
        <Alert
            type={quote.kind === 'downgrade' ? 'warning' : 'info'}
            showIcon
            message={i.t(`billing:quote.${quote.kind}`, params)}
            description={quote.credit_days > 0 && (quote.kind === 'upgrade' || quote.kind === 'downgrade')
                ? i.t(params.from ? 'billing:quote.credit' : 'billing:quote.creditGeneric', params)
                : undefined}
        />
    )
})

const MethodOption = ({ method, icon }: { method: BillingMethod, icon: React.ReactNode }) => (
    <Radio.Button value={method} className="billing-method">
        <span className="billing-method__icon">{icon}</span>
        <span className="billing-method__text">
            <span className="billing-method__title">{i.t(`billing:methods.${method}.title`)}</span>
            <span className="billing-method__hint">{i.t(`billing:methods.${method}.hint`)}</span>
        </span>
    </Radio.Button>
)

const CheckoutModal = observer(() => {
    const checkout = controller.checkout
    const plan = checkout.plan
    const price = controller.price(plan)
    const receipt = !!controller.overview?.receipt
    const offerUrl = controller.overview?.offer_url
    const methods = controller.methods
    const emailOk = !receipt || isEmail(checkout.email)

    const canSubmit = !!price && !!checkout.method && !!checkout.quote && !checkout.quoteLoading && !checkout.quoteError && emailOk

    return (
        <Modal
            open={checkout.open}
            onCancel={() => controller.closeCheckout()}
            title={plan ? i.t('billing:checkout.title', { plan: plan.name }) : ''}
            footer={null}
            width={560}
            destroyOnClose
        >
            {plan && price &&
                <div className="billing-checkout">
                    <div className="billing-checkout__summary">
                        <div>
                            <div className="billing-checkout__label">{i.t('billing:checkout.period')}</div>
                            <Segmented
                                value={controller.months}
                                onChange={(value) => controller.setMonths(Number(value))}
                                options={plan.prices.map((item: any) => ({ value: item.months, label: months(item.months) }))}
                            />
                        </div>
                        <div className="billing-checkout__amount">
                            <div className="billing-checkout__label">{i.t('billing:checkout.toPay')}</div>
                            <div className="billing-checkout__sum">{formatMoney(price.amount, currency())}</div>
                            {price.discount_percent > 0 &&
                                <div className="billing-checkout__save">{i.t('billing:checkout.discount', { percent: price.discount_percent })}</div>
                            }
                        </div>
                    </div>

                    <QuoteExplanation />

                    {methods.length > 0
                        ? <>
                            <div className="billing-checkout__label">{i.t('billing:checkout.method')}</div>
                            <Radio.Group className="billing-methods" value={checkout.method} onChange={(e) => controller.setMethod(e.target.value)}>
                                {methods.includes('card') && <MethodOption method="card" icon={<CreditCardOutlined />} />}
                                {methods.includes('sbp') && <MethodOption method="sbp" icon={<QrcodeOutlined />} />}
                            </Radio.Group>

                            {receipt &&
                                <div className="billing-checkout__email">
                                    <div className="billing-checkout__label">{i.t('billing:checkout.email')}</div>
                                    <Input
                                        size="large"
                                        type="email"
                                        value={checkout.email}
                                        status={checkout.email && !emailOk ? 'error' : undefined}
                                        placeholder="mail@company.ru"
                                        onChange={(e) => controller.setEmail(e.target.value)}
                                    />
                                    <div className="billing-muted">{i.t('billing:checkout.emailHint')}</div>
                                </div>
                            }

                            <Button
                                type="primary"
                                size="large"
                                block
                                className="billing-checkout__pay"
                                loading={checkout.submitting}
                                disabled={!canSubmit}
                                onClick={() => controller.pay()}
                            >
                                {i.t('billing:checkout.pay', { amount: formatMoney(price.amount, currency()) })}
                            </Button>

                            <div className="billing-checkout__secure">
                                <SafetyCertificateOutlined /> {i.t('billing:checkout.secure')}
                            </div>
                            {offerUrl &&
                                <div className="billing-checkout__offer">
                                    {i.t('billing:checkout.offerPrefix')} <a href={offerUrl} target="_blank" rel="noreferrer">{i.t('billing:checkout.offerLink')}</a>
                                </div>
                            }
                        </>
                        : <Alert type="info" showIcon message={i.t('billing:checkout.onlineUnavailable')} />
                    }

                    {controller.overview?.invoice &&
                        <div className="billing-checkout__invoice">
                            <BankOutlined />
                            <span>{i.t('billing:checkout.needInvoice')}</span>
                            <Button type="link" onClick={() => controller.openInvoice(plan)}>{i.t('billing:checkout.requestInvoice')}</Button>
                        </div>
                    }
                </div>
            }
        </Modal>
    )
})

const InvoiceModal = observer(() => {
    const invoice = controller.invoice
    const values = invoice.values
    const plan = invoice.plan
    const price = controller.price(plan)

    const field = (key: keyof typeof values, label: string, props: any = {}) => (
        <div className="billing-field">
            <div className="billing-checkout__label">{label}</div>
            <Input value={values[key]} onChange={(e) => controller.setInvoiceValue(key, e.target.value)} {...props} />
        </div>
    )

    const innOk = /^(\d{10}|\d{12})$/.test(values.inn.replace(/\D+/g, ''))
    const kppOk = values.kpp.trim() === '' || /^\d{9}$/.test(values.kpp.replace(/\D+/g, ''))
    const canSubmit = !!price && values.company.trim() !== '' && innOk && kppOk && isEmail(values.email)

    return (
        <Modal
            open={invoice.open}
            onCancel={() => controller.closeInvoice()}
            title={i.t('billing:invoice.title')}
            okText={i.t('billing:invoice.submit')}
            cancelText={i.t('billing:cancel')}
            onOk={() => controller.submitInvoice()}
            okButtonProps={{ disabled: !canSubmit, loading: invoice.submitting }}
            width={600}
        >
            <div className="billing-invoice">
                <div className="billing-muted">{i.t('billing:invoice.hint')}</div>

                <Row gutter={12}>
                    <Col xs={24} sm={14}>
                        <div className="billing-field">
                            <div className="billing-checkout__label">{i.t('billing:invoice.plan')}</div>
                            <Select
                                style={{ width: '100%' }}
                                value={plan?.level}
                                onChange={(level) => controller.setInvoicePlan(level)}
                                options={controller.plans.map((item: any) => ({ value: item.level, label: item.name }))}
                            />
                        </div>
                    </Col>
                    <Col xs={24} sm={10}>
                        <div className="billing-field">
                            <div className="billing-checkout__label">{i.t('billing:checkout.period')}</div>
                            <Select
                                style={{ width: '100%' }}
                                value={controller.months}
                                onChange={(value) => controller.setMonths(value)}
                                options={(plan?.prices ?? []).map((item: any) => ({ value: item.months, label: months(item.months) }))}
                            />
                        </div>
                    </Col>
                </Row>

                {price
                    ? <div className="billing-invoice__amount">{i.t('billing:invoice.amount', { amount: formatMoney(price.amount, currency()) })}</div>
                    : <Alert type="warning" showIcon message={i.t('billing:plans.noPeriod', { period: months(controller.months) })} />
                }

                {field('company', i.t('billing:invoice.company') + ' *', { placeholder: i.t('billing:invoice.companyPlaceholder') })}
                <Row gutter={12}>
                    <Col xs={24} sm={12}>
                        {field('inn', i.t('billing:invoice.inn') + ' *', { inputMode: 'numeric', maxLength: 12, status: values.inn && !innOk ? 'error' : undefined })}
                    </Col>
                    <Col xs={24} sm={12}>
                        {field('kpp', i.t('billing:invoice.kpp'), { inputMode: 'numeric', maxLength: 9, status: !kppOk ? 'error' : undefined, placeholder: i.t('billing:invoice.kppPlaceholder') })}
                    </Col>
                </Row>
                {field('address', i.t('billing:invoice.address'))}
                <Row gutter={12}>
                    <Col xs={24} sm={12}>
                        {field('email', i.t('billing:invoice.email') + ' *', { type: 'email', status: values.email && !isEmail(values.email) ? 'error' : undefined })}
                    </Col>
                    <Col xs={24} sm={12}>
                        {field('phone', i.t('billing:invoice.phone'))}
                    </Col>
                </Row>
                <div className="billing-field">
                    <div className="billing-checkout__label">{i.t('billing:invoice.comment')}</div>
                    <Input.TextArea rows={2} value={values.comment} maxLength={1000} onChange={(e) => controller.setInvoiceValue('comment', e.target.value)} />
                </div>
            </div>
        </Modal>
    )
})

const History = observer(() => {
    const columns = [
        {
            title: i.t('billing:history.date'),
            key: 'date',
            width: 170,
            render: (text: any, payment: any) => (
                <div>
                    <div>{formatDateTime(payment.created_at)}</div>
                    <div className="billing-muted">{payment.number}</div>
                </div>
            ),
        },
        {
            title: i.t('billing:history.plan'),
            key: 'plan',
            render: (text: any, payment: any) => (
                <div>
                    <div className="billing-strong">{payment.plan_name}</div>
                    <div className="billing-muted">{months(payment.months)}</div>
                </div>
            ),
        },
        {
            title: i.t('billing:history.method'),
            key: 'method',
            render: (text: any, payment: any) => methodLabel(payment),
        },
        {
            title: i.t('billing:history.amount'),
            key: 'amount',
            align: 'right' as const,
            render: (text: any, payment: any) => <span className="billing-strong billing-nowrap">{formatMoney(payment.amount, payment.currency)}</span>,
        },
        {
            title: i.t('billing:history.status'),
            key: 'status',
            render: (text: any, payment: any) => (
                <div>
                    <Tag color={paymentStatusColor(payment.status)}>
                        {payment.method === 'invoice' && payment.status === 'pending'
                            ? i.t('billing:history.invoicePending')
                            : i.t(`billing:history.statuses.${payment.status}`)}
                    </Tag>
                    {payment.status === 'paid' && payment.period_end &&
                        <div className="billing-muted">{i.t('billing:history.until', { date: formatDate(payment.period_end) })}</div>
                    }
                    {payment.error && payment.status === 'failed' &&
                        <Tooltip title={payment.error}><div className="billing-muted billing-hint">{i.t('billing:history.why')}</div></Tooltip>
                    }
                </div>
            ),
        },
        {
            title: '',
            key: 'actions',
            width: 150,
            render: (text: any, payment: any) => {
                if (payment.payment_url) {
                    return <Button size="small" icon={<LinkOutlined />} href={payment.payment_url}>{i.t('billing:history.pay')}</Button>
                }

                if (payment.method === 'invoice' && payment.status === 'pending' && controller.overview?.is_staff) {
                    return (
                        <Popconfirm title={i.t('billing:invoice.confirmQuestion')} onConfirm={() => controller.confirmInvoice(payment.uuid)} okText={i.t('billing:invoice.confirm')} cancelText={i.t('billing:cancel')}>
                            <Button size="small" type="primary">{i.t('billing:invoice.confirm')}</Button>
                        </Popconfirm>
                    )
                }

                return null
            },
        },
    ]

    return (
        <div className="billing-section">
            <div className="billing-section__head">
                <h3 className="billing-section__title">{i.t('billing:history.title')}</h3>
            </div>
            <Table
                rowKey="uuid"
                columns={columns}
                dataSource={controller.payments}
                loading={controller.paymentsLoading}
                scroll={{ x: 760 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={i.t('billing:history.empty')} /> }}
                pagination={controller.pagination.total > controller.pagination.limit ? {
                    current: controller.pagination.page,
                    pageSize: controller.pagination.limit,
                    total: controller.pagination.total,
                    showSizeChanger: false,
                    onChange: (page: number) => controller.loadPayments(page),
                } : false}
            />
        </div>
    )
})

export const Billing = observer(() => {
    const history = useHistory()
    const plansRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        // Параметры читаем один раз при открытии раздела: возврат со страницы оплаты
        // или переход с рабочего стола по кнопке «Продлить» (?renew=1)
        const params = new URLSearchParams(window.location.search)
        const uuid = params.get('payment')
        const renew = params.get('renew') === '1'

        controller.load().then(() => {
            if (!renew) return

            controller.openRenew()
            history.replace('/billing')
        })
        controller.loadPayments(1)

        if (uuid) controller.startResult(uuid, params.get('failed') === '1')

        return () => controller.stopPolling()
    }, [history])

    const closeResult = () => {
        controller.closeResult()
        history.replace('/billing')
    }

    const scrollToPlans = () => plansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })

    const overview = controller.overview

    return (
        <>
            <Titles title={i.t('billing:title')} />

            <div className="billing">
                <PaymentResult onClose={closeResult} />

                {!overview
                    ? controller.loading ? <Skeleton active paragraph={{ rows: 6 }} /> : <Empty description={i.t('billing:errors.load')} />
                    : <>
                        <CurrentSubscription onChoose={scrollToPlans} />
                        <Unavailable />
                        <OpenInvoice />

                        {controller.plans.length > 0 &&
                            <div className="billing-section" ref={plansRef}>
                                <div className="billing-section__head">
                                    <h3 className="billing-section__title">{i.t('billing:plans.title')}</h3>
                                    <PeriodSwitch />
                                </div>
                                <Row gutter={[16, 16]}>
                                    {controller.plans.map((plan: any) => (
                                        <Col key={plan.level} xs={24} sm={12} xl={6}>
                                            <PlanCard plan={plan} />
                                        </Col>
                                    ))}
                                </Row>
                                {overview.invoice &&
                                    <div className="billing-invoice-link">
                                        <BankOutlined />
                                        <span>{i.t('billing:checkout.needInvoice')}</span>
                                        <Button type="link" onClick={() => controller.openInvoice(controller.plans.find((plan: any) => plan.level === overview.subscription?.level) ?? controller.plans[0])}>
                                            {i.t('billing:checkout.requestInvoice')}
                                        </Button>
                                    </div>
                                }
                            </div>
                        }

                        <History />
                    </>
                }
            </div>

            <CheckoutModal />
            <InvoiceModal />
        </>
    )
})

